import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { listWithdrawals, approveWithdrawal, rejectWithdrawal } from '../../api/withdrawalsApi';

function WithdrawalsManagement() {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('requested');
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectNote, setRejectNote] = useState('');

  useEffect(() => {
    fetchWithdrawals();
  }, [filter]);

  const fetchWithdrawals = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const data = await listWithdrawals({ status: filter }, token);
      setWithdrawals(data);
    } catch (error) {
      console.error('Error fetching withdrawals:', error);
      toast.error('Failed to fetch withdrawals');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (withdrawalId, processNow = false) => {
    if (!window.confirm(`${processNow ? 'Process payout immediately' : 'Approve without payout'}?`)) {
      return;
    }

    setActionLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      await approveWithdrawal(withdrawalId, { processNow }, token);
      
      toast.success(processNow ? 
        '✅ Withdrawal processed! Payout sent via Razorpay' : 
        '✅ Withdrawal approved'
      );
      fetchWithdrawals();
      setShowModal(false);
    } catch (error) {
      console.error('Error approving withdrawal:', error);
      toast.error(error.response?.data?.error || 'Failed to approve withdrawal');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (withdrawalId) => {
    if (!rejectNote.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    setActionLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      await rejectWithdrawal(withdrawalId, { adminNote: rejectNote }, token);
      
      toast.success('❌ Withdrawal rejected');
      fetchWithdrawals();
      setShowModal(false);
      setRejectNote('');
    } catch (error) {
      console.error('Error rejecting withdrawal:', error);
      toast.error('Failed to reject withdrawal');
    } finally {
      setActionLoading(false);
    }
  };

  const openModal = (withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setShowModal(true);
    setRejectNote('');
  };

  const formatAmount = (amountBigint) => {
    return `₹${(amountBigint / 100).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      requested: { bg: 'rgba(255, 170, 0, 0.2)', color: '#ffaa00', text: '⏳ Requested' },
      approved: { bg: 'rgba(0, 240, 255, 0.2)', color: '#00f0ff', text: '✓ Approved' },
      processed: { bg: 'rgba(0, 255, 136, 0.2)', color: '#00ff88', text: '✅ Processed' },
      rejected: { bg: 'rgba(255, 51, 102, 0.2)', color: '#ff3366', text: '❌ Rejected' }
    };
    const style = styles[status] || styles.requested;
    
    return (
      <span style={{
        padding: '4px 12px',
        borderRadius: '12px',
        background: style.bg,
        color: style.color,
        fontSize: '0.85rem',
        fontWeight: 'bold'
      }}>
        {style.text}
      </span>
    );
  };

  return (
    <div>
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ 
          fontSize: '2rem', 
          marginBottom: '10px',
          fontFamily: 'Orbitron, sans-serif',
          color: 'var(--primary-color)'
        }}>
          💸 Withdrawal Requests
        </h2>
        <p style={{ color: 'var(--text-secondary)' }}>
          Manage user withdrawal requests and payouts
        </p>
      </div>

      {/* Filter Tabs */}
      <div style={{ 
        display: 'flex', 
        gap: '10px', 
        marginBottom: '30px',
        flexWrap: 'wrap'
      }}>
        {['requested', 'approved', 'processed', 'rejected'].map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`btn ${filter === status ? 'btn-primary' : 'btn-outline'}`}
            style={{ textTransform: 'capitalize' }}
          >
            {status}
          </button>
        ))}
      </div>

      {/* Withdrawals List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <div className="loading-spinner"></div>
          <p>Loading withdrawals...</p>
        </div>
      ) : withdrawals.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '50px' }}>
          <p style={{ color: 'var(--text-secondary)', fontSize: '1.1rem' }}>
            No {filter} withdrawals found
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '20px' }}>
          {withdrawals.map((withdrawal) => (
            <div key={withdrawal.id} className="card">
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                flexWrap: 'wrap',
                gap: '20px'
              }}>
                {/* Left Section */}
                <div style={{ flex: 1, minWidth: '250px' }}>
                  <div style={{ marginBottom: '10px' }}>
                    {getStatusBadge(withdrawal.status)}
                  </div>
                  
                  <h3 style={{ 
                    fontSize: '1.5rem', 
                    color: 'var(--success-color)',
                    marginBottom: '5px'
                  }}>
                    {formatAmount(withdrawal.amount_bigint)}
                  </h3>
                  
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                    <strong>User:</strong> {withdrawal.user_id}
                  </p>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                    <strong>Method:</strong> {withdrawal.method.toUpperCase()}
                  </p>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                    <strong>Requested:</strong> {formatDate(withdrawal.created_at)}
                  </p>
                  
                  {withdrawal.provider_payout_id && (
                    <p style={{ 
                      color: 'var(--primary-color)', 
                      fontSize: '0.85rem',
                      marginTop: '5px'
                    }}>
                      <strong>Payout ID:</strong> {withdrawal.provider_payout_id}
                    </p>
                  )}
                  
                  {withdrawal.admin_note && (
                    <p style={{ 
                      color: 'var(--danger-color)', 
                      fontSize: '0.9rem',
                      marginTop: '10px',
                      padding: '10px',
                      background: 'rgba(255, 51, 102, 0.1)',
                      borderRadius: '4px'
                    }}>
                      <strong>Note:</strong> {withdrawal.admin_note}
                    </p>
                  )}
                </div>

                {/* Right Section - Actions */}
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  gap: '10px',
                  minWidth: '200px'
                }}>
                  <button
                    onClick={() => openModal(withdrawal)}
                    className="btn btn-primary"
                    style={{ width: '100%' }}
                  >
                    View Details
                  </button>
                  
                  {withdrawal.status === 'requested' && (
                    <>
                      <button
                        onClick={() => handleApprove(withdrawal.id, true)}
                        className="btn btn-success"
                        style={{ width: '100%' }}
                        disabled={actionLoading}
                      >
                        💰 Process Payout
                      </button>
                      <button
                        onClick={() => handleApprove(withdrawal.id, false)}
                        className="btn"
                        style={{ 
                          width: '100%',
                          background: 'rgba(0, 240, 255, 0.2)',
                          border: '1px solid var(--primary-color)'
                        }}
                        disabled={actionLoading}
                      >
                        ✓ Approve Only
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && selectedWithdrawal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div className="card" style={{ 
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h3 style={{ color: 'var(--primary-color)' }}>
                Withdrawal Details
              </h3>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-secondary)',
                  fontSize: '1.5rem',
                  cursor: 'pointer'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <p><strong>Amount:</strong> {formatAmount(selectedWithdrawal.amount_bigint)}</p>
              <p><strong>Status:</strong> {getStatusBadge(selectedWithdrawal.status)}</p>
              <p><strong>Method:</strong> {selectedWithdrawal.method}</p>
              <p><strong>User ID:</strong> {selectedWithdrawal.user_id}</p>
            </div>

            {/* Payment Details */}
            <div style={{
              background: 'rgba(0, 240, 255, 0.05)',
              padding: '15px',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <h4 style={{ marginBottom: '10px', color: 'var(--primary-color)' }}>
                Payment Details
              </h4>
              <pre style={{ 
                fontSize: '0.85rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all'
              }}>
                {JSON.stringify(selectedWithdrawal.details, null, 2)}
              </pre>
            </div>

            {/* Actions */}
            {selectedWithdrawal.status === 'requested' && (
              <div>
                <div style={{ marginBottom: '20px' }}>
                  <label className="form-label">Rejection Reason (optional)</label>
                  <textarea
                    className="form-input"
                    rows="3"
                    placeholder="Enter reason for rejection..."
                    value={rejectNote}
                    onChange={(e) => setRejectNote(e.target.value)}
                  />
                </div>

                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button
                    onClick={() => handleApprove(selectedWithdrawal.id, true)}
                    className="btn btn-success"
                    style={{ flex: 1 }}
                    disabled={actionLoading}
                  >
                    {actionLoading ? 'Processing...' : '💰 Process Payout'}
                  </button>
                  <button
                    onClick={() => handleApprove(selectedWithdrawal.id, false)}
                    className="btn btn-primary"
                    style={{ flex: 1 }}
                    disabled={actionLoading}
                  >
                    ✓ Approve Only
                  </button>
                  <button
                    onClick={() => handleReject(selectedWithdrawal.id)}
                    className="btn btn-danger"
                    style={{ flex: 1 }}
                    disabled={actionLoading}
                  >
                    ❌ Reject
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default WithdrawalsManagement;
